import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Save, ExternalLink, Loader2, Plus, Trash2, Upload, Menu as MenuIcon } from "lucide-react";
import { TRANSLATIONS } from "@/lib/i18n";
import { DEFAULT_IMAGES, DEFAULT_CONTACT, DEFAULT_FEATURED, DEFAULT_MENU, normalizeFeatured } from "@/lib/defaults";
import { resolveImg } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rdm_admin_token";
const clone = (o) => JSON.parse(JSON.stringify(o));

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
function deepMerge(base, over) {
  if (!isObj(base) || !isObj(over)) return over === undefined ? base : over;
  const out = { ...base };
  for (const k of Object.keys(over)) {
    out[k] = isObj(base[k]) && isObj(over[k]) ? deepMerge(base[k], over[k]) : over[k];
  }
  return out;
}

const buildDefaultContent = () => ({
  text: {
    hero: clone(TRANSLATIONS.pt.hero),
    manifesto: clone(TRANSLATIONS.pt.manifesto),
    menu: clone(TRANSLATIONS.pt.menu),
    footer: clone(TRANSLATIONS.pt.footer),
  },
  menu: clone(DEFAULT_MENU),
  featured: clone(DEFAULT_FEATURED),
  contact: clone(DEFAULT_CONTACT),
  images: clone(DEFAULT_IMAGES),
});

const CATEGORY_LABELS = TRANSLATIONS.pt.menu.categories;
const GROUP_LABELS = TRANSLATIONS.pt.menu.groups;

/* ------------------------------- UI bits -------------------------------- */
const Field = ({ label, value, onChange, textarea, placeholder }) => (
  <label className="block">
    <span className="mb-1.5 block font-jetmono text-[11px] uppercase tracking-[0.14em] text-[#0D5C63]">{label}</span>
    {textarea ? (
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full rounded-xl border border-[#0D5C63]/20 bg-white px-3.5 py-2.5 text-sm text-[#1A252C] outline-none transition focus:border-[#E76F51] focus:ring-2 focus:ring-[#E76F51]/15"
      />
    ) : (
      <input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-[#0D5C63]/20 bg-white px-3.5 py-2.5 text-sm text-[#1A252C] outline-none transition focus:border-[#E76F51] focus:ring-2 focus:ring-[#E76F51]/15"
      />
    )}
  </label>
);

const SectionCard = ({ title, children }) => (
  <div className="rounded-2xl border border-[#0D5C63]/12 bg-[#FCFBF7] p-5 shadow-sm sm:p-6">
    {title && <h3 className="mb-4 font-cabinet text-lg font-bold text-[#0D5C63]">{title}</h3>}
    <div className="space-y-4">{children}</div>
  </div>
);

/* ------------------------------- Login ---------------------------------- */
function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.access_token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Não foi possível entrar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-[#082F38] px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-3xl border border-[#E9C46A]/20 bg-[#FCFBF7] p-8 shadow-2xl"
      >
        <div className="mb-6 flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[#0D5C63] text-[#FAF6EE]">
            <Lock size={18} />
          </span>
          <div>
            <p className="font-cabinet text-lg font-extrabold text-[#1A252C]">Painel do Proprietário</p>
            <p className="font-jetmono text-[10px] uppercase tracking-[0.2em] text-[#0D5C63]">Rainha do Mar</p>
          </div>
        </div>
        <div className="space-y-4">
          <Field label="E-mail" value={email} onChange={setEmail} placeholder="seu@email.com" />
          <label className="block">
            <span className="mb-1.5 block font-jetmono text-[11px] uppercase tracking-[0.14em] text-[#0D5C63]">Senha</span>
            <input
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-[#0D5C63]/20 bg-white px-3.5 py-2.5 text-sm text-[#1A252C] outline-none transition focus:border-[#E76F51] focus:ring-2 focus:ring-[#E76F51]/15"
            />
          </label>
        </div>
        {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#E76F51] px-6 py-3 font-cabinet font-bold text-[#FAF6EE] transition hover:bg-[#D85C3D] disabled:opacity-60"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />}
          Entrar
        </button>
      </form>
    </div>
  );
}

/* ----------------------------- Image field ------------------------------ */
function ImageUploader({ token, value, onChange, label }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(data.url);
      toast.success("Imagem enviada!");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao enviar a imagem.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4 rounded-2xl border border-[#0D5C63]/12 bg-[#FCFBF7] p-4">
      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-[#0D5C63]/5 ring-1 ring-[#0D5C63]/10">
        {value ? (
          <img src={resolveImg(value)} alt={label} className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center text-[#0D5C63]/40"><Upload size={20} /></div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-cabinet text-sm font-bold text-[#1A252C]">{label}</p>
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className="mt-2 inline-flex items-center gap-2 rounded-full border border-[#0D5C63]/25 px-4 py-1.5 font-cabinet text-xs font-bold text-[#0D5C63] transition hover:bg-[#0D5C63] hover:text-[#FAF6EE] disabled:opacity-60"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          Trocar imagem
        </button>
        <input ref={inputRef} type="file" accept="image/*" onChange={handle} className="hidden" />
      </div>
    </div>
  );
}

/* ------------------------------ Dashboard ------------------------------- */
const TABS = [
  { id: "textos", label: "Textos" },
  { id: "cardapio", label: "Cardápio" },
  { id: "horarios", label: "Horários" },
  { id: "contato", label: "Contato" },
  { id: "imagens", label: "Imagens" },
];

function Dashboard({ token, onLogout }) {
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("textos");
  const [activeCat, setActiveCat] = useState("petiscos");
  const [saving, setSaving] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then((res) => {
        const merged = deepMerge(buildDefaultContent(), res.data || {});
        merged.featured = normalizeFeatured(merged.featured);
        setContent(merged);
      })
      .catch(() => setContent(buildDefaultContent()));
  }, []);

  const update = (fn) => setContent((prev) => { const next = clone(prev); fn(next); return next; });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, { content }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Alterações salvas e publicadas no site!");
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Sessão expirada. Entre novamente.");
        onLogout();
      } else {
        toast.error("Não foi possível salvar. Tente novamente.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!content) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#F4EBD9] text-[#0D5C63]">
        <Loader2 size={30} className="animate-spin" />
      </div>
    );
  }

  const T = content.text;

  return (
    <div className="min-h-screen bg-[#F4EBD9] text-[#1A252C]">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-[#0D5C63]/10 bg-[#082F38] text-[#FAF6EE]">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3.5 sm:px-6">
          <div className="min-w-0">
            <p className="truncate font-cabinet text-base font-extrabold sm:text-lg">Painel · Rainha do Mar</p>
            <p className="hidden font-jetmono text-[10px] uppercase tracking-[0.2em] text-[#E9C46A] sm:block">
              Gerenciamento de conteúdo
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-view-site"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#FAF6EE]/25 px-3.5 py-2 font-cabinet text-xs font-bold transition hover:bg-[#FAF6EE]/10"
            >
              <ExternalLink size={14} /> <span className="hidden sm:inline">Ver site</span>
            </a>
            <button
              onClick={save}
              disabled={saving}
              data-testid="admin-save-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#E76F51] px-4 py-2 font-cabinet text-xs font-bold text-[#FAF6EE] transition hover:bg-[#D85C3D] disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="grid h-9 w-9 place-items-center rounded-full border border-[#FAF6EE]/25 transition hover:bg-[#FAF6EE]/10"
              aria-label="Sair"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-4 pb-2 sm:px-6">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              data-testid={`admin-tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 font-cabinet text-sm font-bold transition ${
                tab === tb.id ? "bg-[#E9C46A] text-[#082F38]" : "text-[#FAF6EE]/70 hover:text-[#FAF6EE]"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-5 px-4 py-6 sm:px-6 sm:py-8">
        {/* TEXTOS */}
        {tab === "textos" && (
          <div className="space-y-5" data-testid="admin-panel-textos">
            <SectionCard title="Destaque (Topo)">
              <Field label="Selo / localização" value={T.hero.badge} onChange={(v) => update((c) => (c.text.hero.badge = v))} />
              <Field label="Título — linha 1" value={T.hero.line1} onChange={(v) => update((c) => (c.text.hero.line1 = v))} />
              <Field label="Título — linha 2 (destaque)" value={T.hero.line2} onChange={(v) => update((c) => (c.text.hero.line2 = v))} />
              <Field label="Subtítulo" textarea value={T.hero.subtitle} onChange={(v) => update((c) => (c.text.hero.subtitle = v))} />
              <div className="grid gap-3 sm:grid-cols-3">
                {T.hero.stats.map((s, i) => (
                  <Field key={i} label={`Selo ${i + 1}`} value={s} onChange={(v) => update((c) => (c.text.hero.stats[i] = v))} />
                ))}
              </div>
            </SectionCard>

            <SectionCard title="Nossa História">
              <Field label="Título" value={T.manifesto.titleA} onChange={(v) => update((c) => (c.text.manifesto.titleA = v))} />
              <Field label="Título (destaque)" value={T.manifesto.titleB} onChange={(v) => update((c) => (c.text.manifesto.titleB = v))} />
              {T.manifesto.chapters.map((ch, i) => (
                <div key={i} className="rounded-xl border border-[#0D5C63]/10 p-4">
                  <p className="mb-3 font-jetmono text-[10px] uppercase tracking-[0.2em] text-[#E76F51]">Capítulo {i + 1}</p>
                  <div className="space-y-3">
                    <Field label="Título" value={ch.title} onChange={(v) => update((c) => (c.text.manifesto.chapters[i].title = v))} />
                    <Field label="Texto" textarea value={ch.text} onChange={(v) => update((c) => (c.text.manifesto.chapters[i].text = v))} />
                  </div>
                </div>
              ))}
            </SectionCard>

            <SectionCard title="Cardápio (introdução)">
              <Field label="Subtítulo da seção" textarea value={T.menu.subtitle} onChange={(v) => update((c) => (c.text.menu.subtitle = v))} />
              <Field label="Descrição do prato em destaque" textarea value={T.menu.featured.desc} onChange={(v) => update((c) => (c.text.menu.featured.desc = v))} />
            </SectionCard>

            <SectionCard title="Rodapé">
              <Field label="Descrição" textarea value={T.footer.blurb} onChange={(v) => update((c) => (c.text.footer.blurb = v))} />
              <Field label="Assinatura" value={T.footer.madeWith} onChange={(v) => update((c) => (c.text.footer.madeWith = v))} />
            </SectionCard>
          </div>
        )}

        {/* CARDÁPIO */}
        {tab === "cardapio" && (
          <div className="space-y-5" data-testid="admin-panel-cardapio">
            <SectionCard title="Prato em destaque">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Nome" value={content.featured.title} onChange={(v) => update((c) => (c.featured.title = v))} />
                <Field label="Preço" value={content.featured.price} onChange={(v) => update((c) => (c.featured.price = v))} />
              </div>
            </SectionCard>

            <div className="flex flex-wrap gap-2">
              {Object.keys(content.menu).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCat(cat)}
                  className={`rounded-full px-4 py-1.5 font-cabinet text-sm font-bold transition ${
                    activeCat === cat ? "bg-[#0D5C63] text-[#FAF6EE]" : "bg-white text-[#0D5C63] hover:bg-[#0D5C63]/10"
                  }`}
                >
                  {CATEGORY_LABELS[cat] || cat}
                </button>
              ))}
            </div>

            {content.menu[activeCat].map((group, gi) => (
              <SectionCard key={group.group} title={GROUP_LABELS[group.group] || group.group}>
                {group.items.map((item, ii) => (
                  <div key={ii} className="rounded-xl border border-[#0D5C63]/10 p-3.5">
                    <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
                      <Field label="Produto" value={item.name} onChange={(v) => update((c) => (c.menu[activeCat][gi].items[ii].name = v))} />
                      <Field label="Preço" value={item.price} onChange={(v) => update((c) => (c.menu[activeCat][gi].items[ii].price = v))} />
                    </div>
                    <div className="mt-3 flex items-end gap-3">
                      <div className="flex-1">
                        <Field
                          label="Descrição (opcional)"
                          value={item.desc?.pt || ""}
                          onChange={(v) =>
                            update((c) => {
                              const it = c.menu[activeCat][gi].items[ii];
                              if (!it.desc) it.desc = { pt: "", en: "", es: "" };
                              it.desc.pt = v;
                            })
                          }
                        />
                      </div>
                      <button
                        onClick={() => update((c) => c.menu[activeCat][gi].items.splice(ii, 1))}
                        className="mb-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-red-200 text-red-500 transition hover:bg-red-50"
                        aria-label="Excluir item"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() =>
                    update((c) => c.menu[activeCat][gi].items.push({ name: "Novo item", desc: null, price: "R$ 0", tags: [] }))
                  }
                  className="inline-flex items-center gap-2 rounded-full border border-[#0D5C63]/25 px-4 py-2 font-cabinet text-xs font-bold text-[#0D5C63] transition hover:bg-[#0D5C63] hover:text-[#FAF6EE]"
                >
                  <Plus size={14} /> Adicionar item
                </button>
              </SectionCard>
            ))}
          </div>
        )}

        {/* HORÁRIOS */}
        {tab === "horarios" && (
          <div className="space-y-5" data-testid="admin-panel-horarios">
            <SectionCard title="Horário de Funcionamento">
              {T.footer.hours.map((line, i) => (
                <div key={i} className="flex items-end gap-3">
                  <div className="flex-1">
                    <Field label={`Linha ${i + 1}`} value={line} onChange={(v) => update((c) => (c.text.footer.hours[i] = v))} />
                  </div>
                  <button
                    onClick={() => update((c) => c.text.footer.hours.splice(i, 1))}
                    className="mb-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-red-200 text-red-500 transition hover:bg-red-50"
                    aria-label="Remover linha"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => update((c) => c.text.footer.hours.push(""))}
                className="inline-flex items-center gap-2 rounded-full border border-[#0D5C63]/25 px-4 py-2 font-cabinet text-xs font-bold text-[#0D5C63] transition hover:bg-[#0D5C63] hover:text-[#FAF6EE]"
              >
                <Plus size={14} /> Adicionar linha
              </button>
            </SectionCard>
          </div>
        )}

        {/* CONTATO */}
        {tab === "contato" && (
          <div className="space-y-5" data-testid="admin-panel-contato">
            <SectionCard title="WhatsApp & Redes">
              <Field
                label="Número do WhatsApp (com DDI, só números)"
                value={content.contact.whatsapp}
                onChange={(v) => update((c) => (c.contact.whatsapp = v.replace(/\D/g, "")))}
                placeholder="5512997044344"
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Link do Instagram" value={content.contact.instagramUrl} onChange={(v) => update((c) => (c.contact.instagramUrl = v))} />
                <Field label="@ do Instagram" value={content.contact.instagramHandle} onChange={(v) => update((c) => (c.contact.instagramHandle = v))} />
              </div>
            </SectionCard>
            <SectionCard title="Endereço">
              <Field label="Linha 1" value={T.footer.addressLine1} onChange={(v) => update((c) => (c.text.footer.addressLine1 = v))} />
              <Field label="Linha 2" value={T.footer.addressLine2} onChange={(v) => update((c) => (c.text.footer.addressLine2 = v))} />
              <Field label="Linha 3" value={T.footer.addressLine3} onChange={(v) => update((c) => (c.text.footer.addressLine3 = v))} />
            </SectionCard>
          </div>
        )}

        {/* IMAGENS */}
        {tab === "imagens" && (
          <div className="space-y-4" data-testid="admin-panel-imagens">
            <ImageUploader token={token} label="Logo central (topo)" value={content.images.heroLogo} onChange={(url) => update((c) => (c.images.heroLogo = url))} />
            <ImageUploader token={token} label="Logo do rodapé" value={content.images.footerLogo} onChange={(url) => update((c) => (c.images.footerLogo = url))} />
            <ImageUploader token={token} label="Prato em destaque (cardápio)" value={content.images.menuFeatured} onChange={(url) => update((c) => (c.images.menuFeatured = url))} />
            {content.images.storyChapters.map((img, i) => (
              <ImageUploader
                key={i}
                token={token}
                label={`Nossa História — foto ${i + 1}`}
                value={img}
                onChange={(url) => update((c) => (c.images.storyChapters[i] = url))}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

/* -------------------------------- Root ---------------------------------- */
export default function Admin() {
  const [token, setToken] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(() => setToken(t))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (checking) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#082F38] text-[#E9C46A]">
        <Loader2 size={30} className="animate-spin" />
      </div>
    );
  }

  return token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />;
}
